import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Search, Loader2 } from "lucide-react";
import api from "@/lib/api";
import LocationPreviewMap from "@/components/portal/LocationPreviewMap";

/**
 * Custom Places Autocomplete using our backend proxy to Google Places API (New).
 * Renders an in-page dropdown of suggestions — no fullscreen expansion, no page change.
 */

function uid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function emptyValue() {
  return {
    formatted_address: "", place_id: "",
    latitude: null, longitude: null,
    maps_url: "", street: "", city: "", state: "", pincode: "", country: "IN",
  };
}

function extractAddress(place) {
  const comp = place.addressComponents || [];
  const get = (type) => comp.find((c) => c.types?.includes(type))?.longText || "";
  const short = (type) => comp.find((c) => c.types?.includes(type))?.shortText || "";
  const lat = place.location?.latitude ?? null;
  const lng = place.location?.longitude ?? null;
  const street = [get("street_number"), get("route")].filter(Boolean).join(" ");
  const city = get("locality") || get("postal_town") || get("administrative_area_level_2");
  return {
    formatted_address: place.formattedAddress || "",
    place_id: place.id || "",
    latitude: typeof lat === "number" ? lat : null,
    longitude: typeof lng === "number" ? lng : null,
    maps_url:
      place.googleMapsUri ||
      (place.id ? `https://www.google.com/maps/place/?q=place_id:${place.id}` : ""),
    street,
    city,
    state: get("administrative_area_level_1"),
    pincode: get("postal_code"),
    country: short("country") || "IN",
  };
}

export const AddressAutocomplete = ({ apiKey, value, onChange }) => {
  const [query, setQuery] = useState(value?.formatted_address || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [manual, setManual] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [error, setError] = useState("");
  const sessionRef = useRef(uid());
  const wrapRef = useRef(null);
  const abortRef = useRef(null);
  const timerRef = useRef(null);
  const skipNextSearchRef = useRef(false);

  useEffect(() => {
    if (manual) return;
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return;
    }
    const q = query.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError("");
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post(
          "/places/autocomplete",
          { input: q, sessionToken: sessionRef.current },
          { signal: ctrl.signal }
        );
        setSuggestions(data.suggestions || []);
        setOpen((data.suggestions || []).length > 0);
        setActiveIdx(-1);
      } catch (e) {
        if (e.name !== "CanceledError" && e.name !== "AbortError" && e.code !== "ERR_CANCELED") {
          setError("Address search unavailable");
          setSuggestions([]);
        }
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ctrl.abort();
    };
  }, [query, manual]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const selectSuggestion = async (s) => {
    skipNextSearchRef.current = true;
    setOpen(false);
    setQuery(s.text || `${s.primary}${s.secondary ? ", " + s.secondary : ""}`);
    setLoading(true);
    try {
      const { data } = await api.get(`/places/details/${encodeURIComponent(s.place_id)}`, {
        params: { session_token: sessionRef.current },
      });
      onChange(extractAddress(data));
      sessionRef.current = uid();
    } catch (e) {
      setError("Could not load address details");
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const set = (key, v) => onChange({ ...value, [key]: v });

  return (
    <div className="space-y-4">
      {!manual && (
        <div ref={wrapRef} className="relative">
          <div className="relative">
            <input
              data-testid="address-autocomplete-input"
              className={`luxury-input ${loading || !query ? "!pr-11" : ""}`}
              placeholder="Search your building or location"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => suggestions.length > 0 && setOpen(true)}
              onKeyDown={onKeyDown}
              autoComplete="off"
            />
            {(loading || !query) && (
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-muted2 pointer-events-none z-10">
                {loading ? (
                  <Loader2 className="animate-spin" size={18} strokeWidth={1.5} />
                ) : (
                  <Search size={18} strokeWidth={1.5} />
                )}
              </div>
            )}
          </div>
          {open && suggestions.length > 0 && (
            <div
              data-testid="places-dropdown"
              className="absolute z-20 left-0 right-0 mt-2 bg-white border border-line rounded-xl shadow-[0_20px_60px_-20px_rgba(44,26,29,0.25)] overflow-hidden"
            >
              {suggestions.map((s, i) => (
                <button
                  key={s.place_id + i}
                  type="button"
                  onClick={() => selectSuggestion(s)}
                  onMouseEnter={() => setActiveIdx(i)}
                  data-testid={`place-suggestion-${i}`}
                  className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors ${
                    activeIdx === i ? "bg-ivory/70" : "hover:bg-ivory/50"
                  } ${i > 0 ? "border-t border-line/60" : ""}`}
                >
                  <MapPin className="text-maroon mt-0.5 flex-shrink-0" size={16} strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ink truncate">
                      {s.primary || s.text}
                    </p>
                    {s.secondary && (
                      <p className="text-xs text-muted2 truncate mt-0.5">{s.secondary}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
          {error && (
            <p
              className="mt-2 text-[11px] text-[#8A2A2A]"
              data-testid="address-search-error"
            >
              {error}
            </p>
          )}
          <p className="mt-2 text-[11px] text-muted2">
            Search and select your building or location from the suggestions.
          </p>
          <button
            type="button"
            onClick={() => setManual(true)}
            className="mt-1 text-[11px] text-muted2 hover:text-maroon transition-colors"
            data-testid="switch-manual-btn"
          >
            Can't find your address? Enter manually
          </button>
        </div>
      )}

      {manual && (
        <div className="rounded-xl border border-line bg-ivory/60 p-4">
          <button
            type="button"
            onClick={() => setManual(false)}
            className="mb-3 text-[11px] text-maroon hover:underline"
            data-testid="switch-search-btn"
          >
            ← Use address search
          </button>
          <p className="text-xs text-muted2 mb-3" data-testid="manual-address-guidance">
            Enter your address manually.
          </p>
          <input
            data-testid="address-street"
            className="luxury-input mb-3"
            placeholder="Street address"
            value={value?.street || ""}
            onChange={(e) => set("street", e.target.value)}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="address-city" className="luxury-input" placeholder="City" value={value?.city || ""} onChange={(e) => set("city", e.target.value)} />
            <input data-testid="address-state" className="luxury-input" placeholder="State" value={value?.state || ""} onChange={(e) => set("state", e.target.value)} />
            <input data-testid="address-pincode" className="luxury-input" placeholder="PIN code" value={value?.pincode || ""} onChange={(e) => set("pincode", e.target.value)} />
            <input data-testid="address-country" className="luxury-input" placeholder="Country" value={value?.country || "IN"} onChange={(e) => set("country", e.target.value)} />
          </div>
        </div>
      )}

      {value?.formatted_address && (
        <motion.div
          key={value.place_id || value.formatted_address}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: "easeOut" }}
          className="rounded-3xl border border-line/70 bg-white p-5 sm:p-6 shadow-[0_10px_36px_-14px_rgba(44,26,29,0.14)]"
          data-testid="address-confirmation-card"
        >
          <p className="overline text-[10px] text-maroon mb-2">✓ Location Selected</p>
          <div className="flex items-start gap-3.5">
            <div className="flex-shrink-0 h-9 w-9 rounded-full bg-maroon/[0.06] flex items-center justify-center">
              <MapPin className="text-maroon" size={17} strokeWidth={1.6} />
            </div>
            <div className="flex-1 min-w-0">
              <p
                className="text-[15px] font-medium text-ink leading-[1.5] break-words"
                data-testid="selected-address"
              >
                {value.formatted_address}
              </p>
              <p className="text-xs text-muted2 mt-1.5 break-words">
                {value.city}
                {value.state ? `, ${value.state}` : ""}
                {value.pincode ? ` — ${value.pincode}` : ""}
              </p>
            </div>
          </div>
          {value.latitude && value.longitude && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.28, delay: 0.08, ease: "easeOut" }}
              className="mt-5 overflow-hidden rounded-2xl border border-line/70 bg-ivory/40"
            >
              <LocationPreviewMap
                apiKey={apiKey}
                latitude={value.latitude}
                longitude={value.longitude}
              />
            </motion.div>
          )}
          {value.maps_url && /^https?:\/\//i.test(String(value.maps_url)) && (
            <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
              <a
                href={value.maps_url}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="open-maps-link"
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-maroon hover:underline"
              >
                Open in Google Maps →
              </a>
              <button
                type="button"
                data-testid="change-location-btn"
                onClick={() => { onChange(emptyValue()); setQuery(""); }}
                className="text-[11px] uppercase tracking-[0.22em] text-muted2 hover:text-maroon transition-colors"
              >
                Change location
              </button>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
